'use client'

import { useEffect, useRef } from 'react'
import { useOrganization, useOrganizationList } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'

/**
 * Component that automatically selects the first available organization
 * if the user doesn't have an active organization selected
 * Also refreshes the app when the organization changes
 */
export default function AutoSelectOrganization() {
  const { organization } = useOrganization()
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  })
  const router = useRouter()
  const prevOrgId = useRef(null)

  useEffect(() => {
    // Only run when data is loaded and user doesn't have an active organization
    if (!isLoaded) return
    
    // If user already has an active organization, do nothing
    if (organization) return

    // If user has organizations, select the first one
    if (userMemberships.data && userMemberships.data.length > 0) {
      const firstOrg = userMemberships.data[0].organization
      setActive({ organization: firstOrg.id })
        .then(() => {
          console.log('Auto-selected organization:', firstOrg.name)
        })
        .catch((err) => {
          console.error('Failed to auto-select organization:', err)
        })
    }
  }, [isLoaded, organization, userMemberships, setActive])

  // Detect organization changes and refresh the app
  useEffect(() => {
    if (!organization) return
    
    // If this is the first load, just store the org ID
    if (prevOrgId.current === null) {
      prevOrgId.current = organization.id
      return
    }
    
    // If organization has changed, refresh the page
    if (prevOrgId.current !== organization.id) {
      console.log('Organization changed, refreshing app...')
      prevOrgId.current = organization.id
      // Force a full page refresh to reset all context and data
      window.location.reload()
    }
  }, [organization, router])

  // This component doesn't render anything
  return null
}
